/* eslint-disable react-refresh/only-export-components */
/**
 * Toast + ToastProvider — FASE 2 / M2.
 *
 * Auditoria de Coerência (2026-05-21) identificou padrão duplicado:
 * `useState<string|null>` + `setTimeout` em cada tela. Duração varia
 * (2,5s vs 3s), estilo varia, posição varia.
 *
 * Padrão recomendado:
 *
 *   const { show } = useToast();
 *
 *   async function handleSave() {
 *     await save();
 *     show('Preferências salvas');                       // success default
 *     show('Erro de conexão', { variant: 'error' });
 *   }
 *
 *   // Render: <ToastProvider> em volta do app (UMA vez no root).
 *
 * Stack visual: múltiplas chamadas empilham na bottom-right (mais recente em cima).
 * Auto-dismiss em 3s default. Click em qualquer toast → dismiss imediato.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { CAMADA } from '@/lib/camadas';

export interface ToastOptions {
  /** 'success' (verde), 'error' (vermelho), 'info' (cinza). Default 'success'. */
  variant?: 'success' | 'error' | 'info';
  /** Duração em ms. Default 3000. */
  durationMs?: number;
}

interface ToastItem extends ToastOptions {
  id: string;
  message: string;
}

interface ToastContextValue {
  show: (message: string, opts?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Hook pra disparar toasts. REQUER <ToastProvider> no tree.
 * Fallback gracioso: se ninguém montou o provider, loga no console.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return {
      show: (_msg: string, _opts?: ToastOptions) => {
        // No-op gracioso quando provider não está montado (tests, etc).
        // Em prod com <ToastProvider> em volta do app, sempre haverá ctx.
      },
    };
  }
  return ctx;
}

const VARIANT_CLASS: Record<NonNullable<ToastOptions['variant']>, string> = {
  success: 'border-success bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1',
  error: 'border-red bg-red-soft/30 dark:bg-red-soft-dark/30 text-ink-1 dark:text-ink-dark-1',
  info: 'border-line dark:border-line-dark bg-paper-warm dark:bg-paper-dark-warm text-ink-1 dark:text-ink-dark-1',
};

const VARIANT_ICON: Record<NonNullable<ToastOptions['variant']>, string> = {
  success: '✅',
  error: '❌',
  info: 'ℹ️',
};

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [items, setItems] = useState<ToastItem[]>([]);
  // INDICADORES-MANUTENCAO (23/10/2026): o timer que fecha o aviso é cancelado quando o provider sai da tela. Sem isso
  // ele disparava DEPOIS, num componente desmontado — no gate, com a máquina carregada, depois de o ambiente do teste ser
  // desmontado ("window is not defined"), e o bloco de testes do src ficava vermelho com todos os testes verdes.
  const timers = useRef(new Set<number>());
  useEffect(() => {
    const ativos = timers.current;
    return () => {
      for (const t of ativos) window.clearTimeout(t);
      ativos.clear();
    };
  }, []);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback<ToastContextValue['show']>(
    (message, opts) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const variant = opts?.variant ?? 'success';
      const duration = opts?.durationMs ?? 3000;
      setItems((prev) => [...prev, { id, message, variant, durationMs: duration }]);
      const t = window.setTimeout(() => {
        timers.current.delete(t);
        dismiss(id);
      }, duration);
      timers.current.add(t);
    },
    [dismiss],
  );

  /**
   * N-1 (2026-07-31): a pilha de avisos vai pro `document.body` via portal e
   * fica na camada `AVISO`, o topo da escada (`src/lib/camadas.ts`).
   *
   * Eram DOIS problemas somados, e um só não explicava o bug:
   *   1. o toast empatava em z-50 com o `Dialog`, que renderiza DEPOIS por usar
   *      portal — empate no z é decidido pela ordem no DOM, e o diálogo ganhava;
   *   2. o toast nascia dentro da árvore do provider, então qualquer ancestral
   *      com `transform`/`filter`/`opacity` criaria um contexto de empilhamento
   *      e o prenderia embaixo, por maior que fosse o número.
   *
   * Portal resolve (2) e a camada resolve (1). Fora do `document.body` não há
   * z-index que garanta nada.
   */
  const pilha = (
    <div
      role="region"
      aria-label="Notificações"
      data-testid="toast-stack"
      style={{ zIndex: CAMADA.AVISO }}
      className="fixed bottom-6 right-6 flex flex-col-reverse gap-2 pointer-events-none"
    >
        {items.map((t) => {
          const variant = t.variant ?? 'success';
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => dismiss(t.id)}
              role="status"
              aria-live="polite"
              data-testid={`toast-${t.id}`}
              data-toast-variant={variant}
              className={`pointer-events-auto px-4 py-3 rounded-xl border shadow-lg text-sm flex items-start gap-2 max-w-sm text-left ${VARIANT_CLASS[variant]}`}
            >
              {/* FX-3: ícone não encolhe; texto quebra (não corta/vaza). */}
              <span aria-hidden className="shrink-0 leading-5">{VARIANT_ICON[variant]}</span>
              <span className="min-w-0 break-words leading-5">{t.message}</span>
            </button>
          );
        })}
    </div>
  );

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      {typeof document !== 'undefined' ? createPortal(pilha, document.body) : pilha}
    </ToastContext.Provider>
  );
}
